/** Public-API credentials manager — Phase 3-pp-apikey.
 *
 * Org admins use this to mint scoped API credentials customers' ERPs
 * embed in their public-API calls. The plaintext secret is shown
 * EXACTLY ONCE on issue; after that only the prefix + last-used metadata
 * are visible. Soft-revoke (DELETE) leaves the row in the list so the
 * audit trail of "which credential made historical calls" stays clean.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { formatDateTime } from "@/lib/format";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { ApiPaths, api } from "@/lib/api";
import type { ApiKey, ApiKeyIssued } from "@/lib/types";

const ALL_SCOPES: string[] = [
  "sessions:read",
  "sessions:write",
  "participants:read",
  "participants:write",
  "evidence:read",
  "recordings:read",
  "audit:read",
  "webhooks:read",
  "webhooks:write",
  "branding:read",
  "branding:write",
  "org:read",
  "whiteboards:read",
];

export function ApiKeysCard() {
  const qc = useQueryClient();
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [scopes, setScopes] = useState<Set<string>>(new Set());
  const [revealed, setRevealed] = useState<ApiKeyIssued | null>(null);

  const list = useQuery({
    queryKey: ["org-api-keys"],
    queryFn: async () => {
      const res = await api.get<ApiKey[]>(ApiPaths.orgApiKeys);
      return res.data;
    },
  });

  const mint = useMutation({
    mutationFn: async () => {
      const res = await api.post<ApiKeyIssued>(ApiPaths.orgApiKeys, {
        name: name.trim(),
        scopes: Array.from(scopes),
      });
      return res.data;
    },
    onSuccess: (issued) => {
      setRevealed(issued);
      setName("");
      setScopes(new Set());
      qc.invalidateQueries({ queryKey: ["org-api-keys"] });
    },
  });

  const revoke = useMutation({
    mutationFn: async (id: string) => {
      await api.delete(ApiPaths.orgApiKey(id));
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["org-api-keys"] }),
  });

  const toggle = (scope: string) =>
    setScopes((prev) => {
      const next = new Set(prev);
      if (next.has(scope)) {
        next.delete(scope);
      } else {
        next.add(scope);
      }
      return next;
    });

  const canMint = name.trim().length > 0 && scopes.size > 0;

  return (
    <section className="card space-y-4" data-testid="api-keys-card">
      <header>
        <h2 className="text-sm font-semibold uppercase tracking-wide text-nb-text-2">
          {t("admin.apiKeys.cardTitle")}
        </h2>
        <p className="text-xs text-nb-text-3">{t("admin.apiKeys.cardHint")}</p>
      </header>

      {revealed && (
        <div
          className="space-y-2 rounded-md border border-cvp-warn bg-cvp-warn/10 px-3 py-2 text-xs text-amber-100"
          role="alert"
          data-testid="api-key-reveal"
        >
          <p className="font-semibold">{t("admin.apiKeys.copySecretNow")}</p>
          <div>
            <div className="text-nb-text">{t("admin.apiKeys.publicKey")}</div>
            <code className="block break-all rounded bg-black/40 px-2 py-1 text-amber-200">
              {revealed.public_key}
            </code>
          </div>
          <div>
            <div className="text-nb-text">{t("admin.apiKeys.secret")}</div>
            <code className="block break-all rounded bg-black/40 px-2 py-1 text-amber-200">
              {revealed.secret}
            </code>
          </div>
          <button
            type="button"
            className="btn-ghost text-xs"
            onClick={() => setRevealed(null)}
          >
            {t("admin.apiKeys.iStoredIt")}
          </button>
        </div>
      )}

      <form
        className="space-y-3"
        onSubmit={(e) => {
          e.preventDefault();
          if (canMint) mint.mutate();
        }}
      >
        <div>
          <label className="label" htmlFor="api-key-name">{t("admin.apiKeys.credentialName")}</label>
          <input
            id="api-key-name"
            className="input"
            value={name}
            placeholder="Acme ERP integration"
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <div>
          <label className="label">{t("admin.apiKeys.scopes")}</label>
          <div className="grid grid-cols-2 gap-1 sm:grid-cols-3">
            {ALL_SCOPES.map((s) => (
              <label
                key={s}
                className="flex items-center gap-2 rounded-md border border-nb-border bg-slate-900/40 px-2 py-1 text-xs text-nb-text"
              >
                <input
                  type="checkbox"
                  checked={scopes.has(s)}
                  onChange={() => toggle(s)}
                />
                <code>{s}</code>
              </label>
            ))}
          </div>
        </div>
        <button
          type="submit"
          className="btn-primary text-sm"
          disabled={!canMint || mint.isPending}
        >
          {mint.isPending ? t("admin.apiKeys.issuing") : t("admin.apiKeys.issueButton")}
        </button>
        {mint.isError && (
          <p className="text-xs text-cvp-danger" role="alert">
            {t("admin.apiKeys.issueError")}
          </p>
        )}
      </form>

      <div className="space-y-2 text-sm">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-nb-text-2">
          {t("admin.apiKeys.activeAndRevoked")}
        </h3>
        {list.isLoading && <p className="text-nb-text-2">{t("common.loading")}</p>}
        {list.data && list.data.length === 0 && (
          <p className="text-nb-text-2">{t("admin.apiKeys.noCredentials")}</p>
        )}
        <ul className="space-y-2">
          {(list.data ?? []).map((k) => (
            <li
              key={k.id}
              className="rounded-md border border-nb-border bg-slate-900/40 px-3 py-2 text-xs"
            >
              <div className="flex items-center justify-between gap-2">
                <span className="font-medium text-nb-text">{k.name}</span>
                <span
                  className={`rounded px-2 py-0.5 ${
                    k.is_active
                      ? "bg-emerald-500/15 text-emerald-300"
                      : "bg-slate-700 text-nb-text-2"
                  }`}
                >
                  {k.is_active ? t("admin.apiKeys.active") : t("admin.apiKeys.revoked")}
                </span>
              </div>
              <div className="mt-1 break-all text-nb-text-2">
                <code>{k.public_key}</code> · secret <code>nb_sec_{k.secret_prefix}…</code>
              </div>
              <div className="mt-1 flex flex-wrap gap-1">
                {k.scopes.map((s) => (
                  <code key={s} className="rounded bg-nb-surface px-1 text-nb-text">
                    {s}
                  </code>
                ))}
              </div>
              <div className="mt-1 text-nb-text-3">
                {t("admin.apiKeys.created", {
                  when: formatDateTime(k.created_at),
                })}
                {k.last_used_at && (
                  <>
                    {" · "}
                    {t("admin.apiKeys.lastUsed", {
                      when: formatDateTime(k.last_used_at),
                    })}
                  </>
                )}
                {k.last_used_ip && (
                  <> {t("admin.apiKeys.from", { ip: k.last_used_ip })}</>
                )}
              </div>
              {k.is_active && (
                <button
                  type="button"
                  className="btn-danger mt-2 text-xs"
                  onClick={() => {
                    if (
                      window.confirm(
                        t("admin.apiKeys.revokeConfirm", { name: k.name }),
                      )
                    ) {
                      revoke.mutate(k.id);
                    }
                  }}
                  disabled={revoke.isPending}
                >
                  {t("common.revoke")}
                </button>
              )}
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
